import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { isNull, isUndefined } from 'lodash';
import validator from 'validator';

export function sortCodeValidator2(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // No error if the field is empty
    }

    const sortCodePattern = /^\d{2}-\d{2}-\d{2}$/;
    const isValid = sortCodePattern.test(value);

    return isValid ? null : { invalidSortCode: { value } };
  };
}

export function sortCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // No error if the field is empty
    }

    const sortCodePattern = /^\d{2}-\d{2}-\d{2}$/;
    const isValidFormat = sortCodePattern.test(value);

    // Remove non-digit characters (like dashes), then check if it's only zeros
    const digitsOnly = value.replace(/\D/g, '');
    const isOnlyZeros = /^0+$/.test(digitsOnly);
    const isTooLong = digitsOnly.length > 6;

    if (!isValidFormat || isOnlyZeros || isTooLong) {
      return { invalidSortCode: { value } };
    }

    return null;
  };
}

export function customEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value;
    if (!email) {
      return null; // Skip validation if the field is empty
    }

    // Stricter email regex to allow valid special characters in the local part
    const strictEmailRegex =
      /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9-]+\.[a-zA-Z]{2,15}$/;

    return strictEmailRegex.test(email)
      ? null
      : { invalidEmail: 'Invalid email format' };
  };
}

export function noneZeroValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      isNull(control.value) ||
      isUndefined(control.value) ||
      control.value === ''
    )
      return null;

    const isValid = +control.value !== 0;
    const message = {
      noneZero: { message: 'The field value can not be 0' },
    };
    return isValid ? null : message;
  };
}

/**
 * Creates a validator that allows only specific characters.
 * @param allowedPattern A regex that defines which characters are allowed. E.g., /[A-Za-z0-9\s\-]/ for alphanumerics, spaces, and dashes.
 */
export function specialCharacterValidator2(
  allowedPattern: RegExp,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (!value) return null; // Skip validation if empty

    const disallowedPattern = new RegExp(`[^${allowedPattern.source}]`);

    return disallowedPattern.test(value)
      ? { specialCharacterError: { allowed: allowedPattern.source } }
      : null;
  };
}

export function specialCharacterValidator(allowedRegex: RegExp): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (!value) return null;

    for (const char of value) {
      if (!allowedRegex.test(char)) {
        // return { specialCharacterError: true };
        return {
          specialCharacterError: {
            allowed: getAllowedCharactersMessage(allowedRegex),
          },
        };
      }
    }

    return null;
  };
}

export function getAllowedCharactersMessage2(allowedRegex: RegExp): string {
  const pattern = allowedRegex.source;

  const parts: string[] = [];

  if (pattern.includes('A-Z')) parts.push('A-Z');
  if (pattern.includes('a-z')) parts.push('a-z');
  if (pattern.includes('0-9')) parts.push('0-9');
  if (pattern.includes('\\-')) parts.push('dash (-)');
  if (pattern.includes('_')) parts.push('underscore (_)');
  if (pattern.includes('\\.')) parts.push('dot (.)');
  if (pattern.includes('\\s')) parts.push('space');
  if (pattern.includes('@')) parts.push('at (@)');
  if (pattern.includes(',')) parts.push('comma (,)');

  return `Only ${parts.join(', ').replace(/, ([^,]*)$/, ' and $1')} are allowed.`;
}

export function getAllowedCharactersMessage(allowedRegex: RegExp): string {
  const pattern = allowedRegex.source;

  // Match characters inside the square brackets, e.g. A-Za-z0-9\-_*
  const match = pattern.match(/\[([^\]]+)\]/);
  if (!match) return 'Allowed characters are not defined.';

  const charClass = match[1];
  const descriptions: string[] = [];

  // Match known ranges and characters
  if (charClass.includes('A-Z')) descriptions.push('uppercase letters (A-Z)');
  if (charClass.includes('a-z')) descriptions.push('lowercase letters (a-z)');
  if (charClass.includes('0-9')) descriptions.push('digits (0-9)');

  // Check for individual characters
  const specials: { [key: string]: string } = {
    '\\-': 'dash (-)',
    '-': 'dash (-)',
    _: 'underscore (_)',
    '\\.': 'dot (.)',
    '\\s': 'space',
    '*': 'asterisk (*)',
    '@': 'at (@)',
    '#': 'hash (#)',
    ',': 'comma (,)',
  };

  // Loop over each character (after ranges)
  for (const char of charClass.replace(/(A-Z|a-z|0-9)/g, '')) {
    const description = specials[char] || specials[`\\${char}`];
    if (description && !descriptions.includes(description)) {
      descriptions.push(description);
    }
  }

  // Final readable output
  return `Only ${descriptions.join(', ').replace(/, ([^,]*)$/, ' and $1')} are allowed.`;
}

export const VALIDATOR_ONLY_SPECIAL_CHARACTERS =
  Validators.pattern('.*[A-Za-z0-9].*');

export const VALIDATOR_NOT_ONLY_NUMBERS = Validators.pattern('^(?!\\d+$).*$');

export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      isNull(control.value) ||
      isUndefined(control.value) ||
      control.value === ''
    )
      return null;

    const isValid = validator.isIBAN(control.value);
    const message = {
      invalidIban: { message: 'Invalid IBAN' },
    };
    return isValid ? null : message;
  };
}
